// src/components/DeepLinkListener.jsx
import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginWithToken } from '../store/slices/authSlice';
import { signInWithBackendToken } from '../firebase';  // ← make sure this import is here
import { Capacitor } from '@capacitor/core';

// Your app's custom schemes / redirect URIs:
const KAKAO_REVERSED_SCHEME = `kakaoa2e5d7e4428ee5a5fbc5c79c505aa56d://oauth`;
const GOOGLE_REVERSED_SCHEME = `com.googleusercontent.apps.657266059621-9khh7nolhotok3la8uc3u8a9bc2hi7rr:/oauth`;
const SPOTIFY_SCHEME = `how2play://oauth/spotify`;

// For Kakao mobile you need to send the exact web redirect URI registered in the console:
const KAKAO_WEB_REDIRECT_URI =
  'https://fastapi-backend-657266059621.asia-northeast1.run.app/api/auth/callback/kakao';

const DeepLinkListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    let listenerHandle = null;

    const initializeListener = async () => {
      listenerHandle = await App.addListener('appUrlOpen', async (data) => {
      try {
        console.log('🔗 Deep link received:', data.url);

        if (data.url.startsWith(KAKAO_REVERSED_SCHEME)) {
          await handleOAuthCallback(data.url, 'kakao');
        } else if (data.url.startsWith(GOOGLE_REVERSED_SCHEME)) {
          await handleOAuthCallback(data.url, 'google');
        } else if (data.url.startsWith(SPOTIFY_SCHEME)) {
          await handleOAuthCallback(data.url, 'spotify');
        }

        // close the in-app browser once handled
        await Browser.close();
      } catch (error) {
        console.error('❌ Error handling deep link:', error);
      }
    });
    };

    initializeListener();

    return () => {
      if (listenerHandle && typeof listenerHandle.remove === 'function') {
        listenerHandle.remove();
      }
    };
  }, [dispatch, navigate]);

  // Parse the URL, extract `code`, and exchange it
  const handleOAuthCallback = async (url, provider) => {
    const [, queryString] = url.split('?');
    if (!queryString) return;

    const params = new URLSearchParams(queryString);
    const code = params.get('code');
    if (code) {
      console.log(`🔑 Received ${provider} auth code:`, code);
      await exchangeAuthCodeForToken(code, provider);
    } else {
      console.warn(`⚠️ No 'code' param found for provider=${provider}`);
    }
  };

  // Exchange the code for your FastAPI JWT, store it in Redux, then sign in to Firebase
  const exchangeAuthCodeForToken = async (code, provider) => {
    try {
      const requestBody = { code };
      const isNative = Capacitor.isNativePlatform();

      if (provider === 'google') {
        const codeVerifier = localStorage.getItem('google_code_verifier');
        if (!codeVerifier) {
          console.error('⚠️ No stored code_verifier found for Google!');
          navigate('/login', { replace: true, state: { error: 'Google login error: missing verifier.' } });
          return;
        }
        requestBody.code_verifier = codeVerifier;
      }

      if (provider === 'kakao' && isNative) {
        // 네이티브 카카오 로그인의 경우, 백엔드가 KAKAO_MOBILE_REDIRECT_URI를 사용하도록
        // DeepLinkListener가 받은 redirect_uri를 백엔드에 전달할 수 있습니다 (필요시).
        // 현재 백엔드 /api/auth/callback/kakao POST는 redirect_uri를 payload에서 받을 수 있게 되어있음.
        // 하지만 일반적으로는 카카오 개발자 콘솔에 등록된 redirect_uri와 일치하는 것을 서버에서 사용.
        // 필요하다면 여기서 requestBody.redirect_uri = KAKAO_WEB_REDIRECT_URI; (또는 모바일용 URI) 추가.
        // 지금은 백엔드가 환경변수 또는 기본값으로 처리한다고 가정.
      }

      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/callback/${provider}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }
      );
      const data = await resp.json();
      console.log(`✅ ${provider} Mobile Backend Response:`, data);

      if (!data.access_token || !data.user) {
        console.error(`❌ No access_token or user data returned for ${provider}!`, data);
        navigate('/login', { replace: true, state: { error: `${provider} login error: No token/user.` } });
        return;
      }

      // 1) Store FastAPI JWT + user in Redux. Pass refresh_token for native.
      // data.refresh_token은 백엔드의 mobile callback에서 반환됨.
      const { user, token, refreshToken } = await dispatch(loginWithToken({
        token: data.access_token,
        userFromCallback: data.user,
        refreshTokenForNative: data.refresh_token
      })).unwrap();

      // 2) Immediately sign into Firebase with your custom token
      // signInWithBackendToken는 access_token (FastAPI JWT)을 사용합니다.
      await signInWithBackendToken(token, user.name);
      console.log('✅ Firebase sign-in successful from DeepLinkListener.');

      // 3) Navigate based on whether phone is verified
      if (!user.phone) {
        console.log('📞 No phone number found. Navigating to phone-verification.');
        navigate('/phone-verification', { replace: true });
      } else {

        navigate('/home', { replace: true });
      }
    } catch (err) {
      console.error(`❌ Error in exchangeAuthCodeForToken (${provider}):`, err);
      let errorMessage = `${provider} login failed. Please try again.`;
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      navigate('/login', { replace: true, state: { error: errorMessage } });
    }
  };

  return null;
};

export default DeepLinkListener;
